import { useState, useEffect } from "react";
import { User, sendEmailVerification, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePreviewMode } from "../context/PreviewModeContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { fetchMe, fetchMyStats, patchDisplayName, DbUser, UserStats } from "../api/users";
import { authErrorKey } from "../lib/authErrors";
import UserStatsCard from "../components/UserStatsCard";
import {
  card,
  widePageContainer,
  primaryBtn,
  secondaryBtn,
  inputStyle,
  errorStyle,
  pageTitle,
  panelHeading,
} from "../styles";

interface Props {
  user: User;
  onDisplayNameChange: (name: string) => void;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "1rem",
        padding: "0.55rem 0",
        borderBottom: "1px solid var(--color-border-subtle)",
      }}
    >
      <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500, fontSize: "0.95rem", textAlign: "right" }}>{children}</span>
    </div>
  );
}


export default function ProfilePage({ user, onDisplayNameChange }: Props) {
  const { t } = useTranslation();
  const { previewMode } = usePreviewMode();
  const isMobile = useIsMobile();
  useBreadcrumbs([{ label: t("common.home"), to: "/overview" }, { label: t("profile.title") }]);

  const [profile, setProfile] = useState<DbUser | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const hasPasswordProvider = user.providerData.some((p) => p.providerId === "password");

  useEffect(() => {
    Promise.all([fetchMe(user), fetchMyStats(user)])
      .then(([me, myStats]) => {
        setProfile(me);
        setDisplayName(me.displayName ?? "");
        setStats(myStats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleSave = async () => {
    const trimmed = displayName.trim();
    if (trimmed.length < 2) { setSaveError(t("profile.errors.minLength")); return; }
    if (trimmed.length > 50) { setSaveError(t("profile.errors.maxLength")); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await patchDisplayName(user, trimmed);
      setProfile((prev) => prev ? { ...prev, displayName: updated.displayName } : prev);
      setDisplayName(updated.displayName);
      onDisplayNameChange(updated.displayName);
      setEditing(false);
    } catch {
      setSaveError(t("common.somethingWentWrong"));
    } finally {
      setSaving(false);
    }
  };

  const handleResendVerification = async () => {
    setVerificationSending(true);
    try {
      await sendEmailVerification(user);
      setVerificationSent(true);
    } catch { /* Firebase rate-limits this natively */ }
    finally { setVerificationSending(false); }
  };

  const handleChangePassword = async () => {
    setPwError(null);
    if (newPassword.length < 6) { setPwError(t("profile.security.errors.minLength")); return; }
    if (newPassword !== confirmPassword) { setPwError(t("profile.security.errors.mismatch")); return; }

    setPwSaving(true);
    try {
      const credential = EmailAuthProvider.credential(user.email!, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChangingPassword(false);
      setPwSuccess(true);
    } catch (err: any) {
      setPwError(t(authErrorKey(err.code)));
    } finally {
      setPwSaving(false);
    }
  };

if (loading) return <div style={{ padding: "2rem" }}>{t("common.loading")}</div>;
  if (!profile) return <div style={{ padding: "2rem" }}>{t("common.somethingWentWrong")}</div>;

  const avatarLetter = profile.displayName ? profile.displayName.charAt(0).toUpperCase() : "?";

  return (
    <div style={{ ...widePageContainer, padding: "1.5rem 0" }}>

      {/* Page header card — full width */}
      <div style={{ ...card, marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: "1.25rem" }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--color-accent)", color: "var(--color-btn-primary-text)",
          fontSize: "1.8rem", fontWeight: 700, fontFamily: "var(--font-display)",
        }}>
          {avatarLetter}
        </div>
        <div>
          <div style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)", marginBottom: "0.3rem" }}>
            {t("profile.title")}
          </div>
          <h1 style={{ ...pageTitle, fontSize: "1.6rem" }}>{profile.displayName}</h1>
        </div>
      </div>

      {/* Two-column grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 280px", gap: "1.5rem", alignItems: "start" }}>

        {/* Left: account details + theme */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* ── Account details ── */}
      <section style={card}>
        <h2 style={panelHeading}>
          {t("profile.accountDetails")}
        </h2>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "1rem",
            padding: "0.55rem 0",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", flexShrink: 0 }}>
            {t("profile.displayName")}
          </span>
          <div style={{ textAlign: "right", flex: 1 }}>
            {editing ? (
              <div>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
                  aria-label={t("profile.displayName")}
                  style={{ ...inputStyle, marginBottom: "0.5rem" }}
                  autoFocus
                />
                {saveError && <p role="alert" style={errorStyle}>{saveError}</p>}
                <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                  <button onClick={handleSave} disabled={saving} style={primaryBtn}>
                    {saving ? t("common.saving") : t("common.save")}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setDisplayName(profile.displayName); setSaveError(null); }}
                    style={secondaryBtn}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", justifyContent: "flex-end" }}>
                <span style={{ fontWeight: 500, fontSize: "0.95rem" }}>{profile.displayName}</span>
                <button
                  onClick={() => setEditing(true)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-link)", fontSize: "0.85rem", padding: 0 }}
                >
                  {t("common.edit")}
                </button>
              </div>
            )}
          </div>
        </div>

        <DetailRow label={t("profile.email")}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
            <span>{profile.email}</span>
            {profile.emailVerified ? (
              <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 99, background: "color-mix(in srgb, var(--color-success) 15%, transparent)", border: "1px solid var(--color-success)", color: "var(--color-success)" }}>Verified</span>
            ) : (
              <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 99, background: "color-mix(in srgb, oklch(0.80 0.15 55) 12%, transparent)", border: "1px solid oklch(0.80 0.15 55)", color: "oklch(0.80 0.15 55)" }}>Unverified</span>
            )}
          </div>
        </DetailRow>
        {!profile.emailVerified && (
          <div style={{ padding: "0.5rem 0", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
            <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
              {verificationSent ? t("profile.verificationSent") : t("profile.verifyEmail")}
            </span>
            {!verificationSent && (
              <button onClick={handleResendVerification} disabled={verificationSending} style={{ ...secondaryBtn, fontSize: "0.8rem", padding: "0.3rem 0.75rem", flexShrink: 0 }}>
                {verificationSending ? "Sending…" : "Send verification email"}
              </button>
            )}
          </div>
        )}
        <DetailRow label={t("profile.membershipType")}>
          {profile.role === "ADMIN" && !previewMode ? (
            <span style={{ color: "var(--color-accent)", fontWeight: 600 }}>{t("profile.membershipAdmin")}</span>
          ) : (
            t("profile.membershipStandard")
          )}
        </DetailRow>
      </section>

      {/* ── Security ── */}
      <section style={card}>
        <h2 style={panelHeading}>
          {t("profile.security.title")}
        </h2>
        <div>
          {!hasPasswordProvider ? (
            <p style={{ padding: "0.75rem 0", margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              {t("profile.security.googleOnlyNotice")}
            </p>
          ) : changingPassword ? (
            <div style={{ padding: "0.75rem 0" }}>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>
                {t("profile.security.currentPassword")}
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPwError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
                autoFocus
              />
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>
                {t("profile.security.newPassword")}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPwError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
              />
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>
                {t("profile.security.confirmPassword")}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPwError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
              />
              {pwError && <p role="alert" style={errorStyle}>{pwError}</p>}
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                <button onClick={handleChangePassword} disabled={pwSaving} style={primaryBtn}>
                  {pwSaving ? t("profile.security.saving") : t("profile.security.save")}
                </button>
                <button
                  onClick={() => {
                    setChangingPassword(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setPwError(null);
                  }}
                  style={secondaryBtn}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <DetailRow label={t("profile.security.password")}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", justifyContent: "flex-end" }}>
                {pwSuccess && <span style={{ fontSize: "0.78rem", color: "var(--color-success)" }}>{t("profile.security.success")}</span>}
                <span style={{ fontWeight: 500, fontSize: "0.95rem", letterSpacing: "0.15em" }}>········</span>
                <button
                  onClick={() => { setChangingPassword(true); setPwSuccess(false); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-link)", fontSize: "0.85rem", padding: 0 }}
                >
                  {t("profile.security.change")}
                </button>
              </div>
            </DetailRow>
          )}
        </div>
      </section>

        </div>{/* end left column */}

        {/* Right: stats */}
        {stats && <UserStatsCard stats={stats} createdAt={profile.createdAt} />}

      </div>{/* end grid */}
    </div>
  );
}
